import { useState, useEffect } from 'react'
import api from '../../lib/api'
import { useBrand } from '@/hooks/useBrand'
import { formatPhoneDisplay } from '@/lib/utils'
import {
  Phone,
  Mic,
  Search,
  Plus,
  Settings,
  CheckCircle,
  XCircle,
  AlertCircle,
  PhoneIncoming,
  PhoneOutgoing,
  Clock,
  DollarSign,
  RefreshCw,
  Zap,
  Activity
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PageHeader } from '@/components/dashboard-ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'

interface VoiceOverview {
  phoneNumbers: {
    total: number
    active: number
    pending: number
  }
  assistants: {
    total: number
  }
  calls: {
    total: number
    thisMonth: number
    lastMonth: number
    minutesThisMonth: number
    completed: number
    missed: number
  }
  costs: {
    voiceCostThisMonth: number
    phoneCostThisMonth: number
    avgCostPerCall: number
  }
  organizationsWithVoice: number
  voiceConfigured: boolean
}

interface OrganizationVoice {
  id: string
  name: string
  slug: string
  status: string
  subscriptionTier: string
  voiceEnabled: boolean
  callDirection: string | null
  phoneNumber: string | null
  phoneCount: number
  assistantCount: number
  callsThisMonth: number
  minutesThisMonth: number
  voiceCostThisMonth: number
  phoneCost: number
}

interface OrgVoiceConfig {
  organization: {
    id: string
    name: string
    voiceEnabled: boolean
    callDirection: string | null
    limits: {
      monthlyVoiceMinutes: number
      phoneNumbers: number
    }
  }
  phoneNumbers: any[]
  assistants: any[]
  recentCalls: any[]
}

function ProviderStatusCard() {
  const [provider, setProvider] = useState<any>(null)
  const [isTesting, setIsTesting] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    api.get('/api/platform/voice-provider').then(res => {
      if (res.data) setProvider(res.data)
    }).catch(() => {})
  }, [])

  const testTelnyx = async () => {
    setIsTesting(true)
    try {
      const res = await api.post('/api/platform/voice-provider/test-telnyx')
      toast({ title: 'Telnyx connectivity test passed', description: `Agent ID: ${res.data.assistantId}` })
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Telnyx test failed', description: err.response?.data?.message || err.message })
    } finally {
      setIsTesting(false)
    }
  }

  if (!provider) return null

  return (
    <Card className="card-inset">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Voice Provider</CardTitle>
          </div>
          <Badge variant={provider.current === 'telnyx' ? 'default' : 'secondary'}>
            {provider.current === 'telnyx' ? 'Voice' : provider.current}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            {provider.available?.telnyx ? (
              <CheckCircle className="h-4 w-4 text-green-500" />
            ) : (
              <XCircle className="h-4 w-4 text-red-500" />
            )}
            <span className="text-sm">Telnyx</span>
          </div>
          {/* Legacy Twilio+ElevenLabs provider hidden — Telnyx is primary */}
          <div className="ml-auto">
            <Button variant="outline" size="sm" onClick={testTelnyx} disabled={isTesting || !provider.available?.telnyx}>
              <Zap className={`mr-2 h-3 w-3 ${isTesting ? 'animate-pulse' : ''}`} />
              {isTesting ? 'Testing...' : 'Test Telnyx'}
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Default provider is set via <code className="bg-muted px-1 rounded">DEFAULT_VOICE_PROVIDER</code> on Railway. Currently: <strong>{provider.current}</strong>
        </p>
      </CardContent>
    </Card>
  )
}

export default function VoiceManagementPage() {
  const brand = useBrand()
  const [overview, setOverview] = useState<VoiceOverview | null>(null)
  const [organizations, setOrganizations] = useState<OrganizationVoice[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(true)

  // Dialog states
  const [selectedOrg, setSelectedOrg] = useState<OrganizationVoice | null>(null)
  const [orgConfig, setOrgConfig] = useState<OrgVoiceConfig | null>(null)
  const [showConfigDialog, setShowConfigDialog] = useState(false)
  const [showProvisionDialog, setShowProvisionDialog] = useState(false)
  const [showAssistantDialog, setShowAssistantDialog] = useState(false)

  // Form states
  const [provisionForm, setProvisionForm] = useState({
    areaCode: '',
    callDirection: 'both',
    preferredProvider: 'telnyx' as 'telnyx'
  })
  const [assistantForm, setAssistantForm] = useState({
    name: '',
    firstMessage: '',
    systemPrompt: '',
    voiceId: 'jennifer'
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setIsLoading(true)
    try {
      const [overviewRes, orgsRes] = await Promise.all([
        api.get('/api/platform/voice/overview'),
        api.get('/api/platform/voice/organizations')
      ])

      if (overviewRes.data.success) {
        setOverview(overviewRes.data.overview)
      }
      if (orgsRes.data.success) {
        setOrganizations(orgsRes.data.organizations)
      }
    } catch (error) {
      console.error('Error loading voice data:', error)
      toast({
        title: 'Error',
        description: 'Failed to load voice management data',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const loadOrgConfig = async (org: OrganizationVoice) => {
    try {
      const response = await api.get(`/api/platform/organizations/${org.id}/voice`)
      if (response.data.success) {
        setOrgConfig(response.data)
        setSelectedOrg(org)
        setShowConfigDialog(true)
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load organization voice config',
        variant: 'destructive'
      })
    }
  }

  const toggleVoice = async (orgId: string, enabled: boolean) => {
    try {
      await api.put(`/api/platform/organizations/${orgId}/voice/enable`, { enabled })
      toast({
        title: 'Success',
        description: `Voice ${enabled ? 'enabled' : 'disabled'} for organization`
      })
      loadData()
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update voice settings',
        variant: 'destructive'
      })
    }
  }

  const provisionNumber = async () => {
    if (!selectedOrg) return

    setIsSubmitting(true)
    try {
      await api.post(`/api/platform/organizations/${selectedOrg.id}/voice/number/provision`, provisionForm)
      toast({
        title: 'Success',
        description: 'Phone number provisioned successfully'
      })
      setShowProvisionDialog(false)
      setProvisionForm({ areaCode: '', callDirection: 'both', preferredProvider: 'telnyx' })
      loadData()
      if (showConfigDialog) {
        loadOrgConfig(selectedOrg)
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to provision phone number',
        variant: 'destructive'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const createAssistant = async () => {
    if (!selectedOrg) return

    setIsSubmitting(true)
    try {
      await api.post(`/api/platform/organizations/${selectedOrg.id}/voice/assistant`, assistantForm)
      toast({
        title: 'Success',
        description: 'Assistant created successfully'
      })
      setShowAssistantDialog(false)
      setAssistantForm({ name: '', firstMessage: '', systemPrompt: '', voiceId: 'jennifer' })
      loadData()
      if (showConfigDialog) {
        loadOrgConfig(selectedOrg)
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to create assistant',
        variant: 'destructive'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const syncCosts = async () => {
    setIsSyncing(true)
    try {
      const response = await api.post('/api/platform/costs/sync')
      if (response.data.success) {
        toast({
          title: 'Cost sync complete',
          description: `${response.data.synced} calls synced, ${response.data.errors} errors`
        })
        loadData()
      }
    } catch (error: any) {
      toast({
        title: 'Sync failed',
        description: error.response?.data?.message || 'Failed to sync costs',
        variant: 'destructive'
      })
    } finally {
      setIsSyncing(false)
    }
  }

  const filteredOrgs = organizations.filter(org =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.slug.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const formatPhoneNumber = (phone: string | null) => formatPhoneDisplay(phone)

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
        <div className="h-96 bg-muted animate-pulse rounded-lg" />
      </div>
    )
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <PageHeader
        eyebrow="The Castle"
        title="Voice & Costs"
        subtitle="Manage voice calling and monitor costs across all organizations."
        actions={
          <>
            <Button
              variant="outline"
              onClick={syncCosts}
              disabled={isSyncing}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Syncing...' : 'Sync Costs'}
            </Button>
            {overview && !overview.voiceConfigured && (
              <Badge variant="destructive" className="flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Voice Provider Not Configured
              </Badge>
            )}
          </>
        }
      />

      {/* Provider Status */}
      <ProviderStatusCard />

      {/* Overview Cards */}
      {overview && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Phone Numbers</CardTitle>
                <Phone className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{overview.phoneNumbers.total}</div>
                <p className="text-xs text-muted-foreground">
                  {overview.phoneNumbers.active} active, {overview.phoneNumbers.pending} pending
                </p>
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">AI Assistants</CardTitle>
                <Mic className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{overview.assistants.total}</div>
                <p className="text-xs text-muted-foreground">
                  Across {overview.organizationsWithVoice} organizations
                </p>
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Calls This Month</CardTitle>
                <PhoneIncoming className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{overview.calls.thisMonth}</div>
                <p className="text-xs text-muted-foreground">
                  {overview.calls.completed} completed, {overview.calls.missed} missed
                </p>
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Voice Minutes</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{overview.calls.minutesThisMonth}</div>
                <p className="text-xs text-muted-foreground">
                  {overview.calls.total} total calls all time
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Cost Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Voice Cost This Month</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${overview.costs.voiceCostThisMonth.toFixed(2)}</div>
                <p className="text-xs text-muted-foreground">
                  from {overview.calls.thisMonth} calls
                </p>
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Phone Cost This Month</CardTitle>
                <Phone className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${overview.costs.phoneCostThisMonth.toFixed(2)}</div>
                <p className="text-xs text-muted-foreground">
                  {overview.phoneNumbers.active} active numbers
                </p>
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Cost/Call</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${overview.costs.avgCostPerCall.toFixed(2)}</div>
                <p className="text-xs text-muted-foreground">
                  this month average
                </p>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Organizations Table */}
      <Card className="card-inset">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Organizations</CardTitle>
              <CardDescription>Voice configuration by organization</CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Voice</TableHead>
                <TableHead className="hidden sm:table-cell">Phone Number</TableHead>
                <TableHead className="hidden md:table-cell">Calls (Month)</TableHead>
                <TableHead className="hidden md:table-cell">Minutes</TableHead>
                <TableHead className="hidden md:table-cell">Cost</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrgs.map((org) => (
                <TableRow key={org.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{org.name}</p>
                      <p className="text-xs text-muted-foreground">{org.slug}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={org.status === 'active' ? 'default' : 'secondary'}>
                      {org.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={org.voiceEnabled}
                        onCheckedChange={(checked) => toggleVoice(org.id, checked)}
                        disabled={!!(overview && !overview.voiceConfigured)}
                        title={overview && !overview.voiceConfigured ? 'Voice provider not configured — set TELNYX_API_KEY in environment variables' : undefined}
                      />
                      {overview && !overview.voiceConfigured ? (
                        <span className="text-xs text-amber-500" title="Set TELNYX_API_KEY to enable">Not configured</span>
                      ) : org.voiceEnabled ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    {org.phoneNumber ? (
                      <span className="font-mono text-sm">{formatPhoneNumber(org.phoneNumber)}</span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{org.callsThisMonth}</TableCell>
                  <TableCell className="hidden md:table-cell">{org.minutesThisMonth}</TableCell>
                  <TableCell className="hidden md:table-cell font-mono text-sm">
                    {(org.voiceCostThisMonth + org.phoneCost) > 0
                      ? `$${(org.voiceCostThisMonth + org.phoneCost).toFixed(2)}`
                      : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => loadOrgConfig(org)}>
                      <Settings className="h-4 w-4 mr-1" />
                      Configure
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredOrgs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No organizations found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Organization Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedOrg?.name} - Voice Configuration</DialogTitle>
            <DialogDescription>
              Manage voice settings for this organization
            </DialogDescription>
          </DialogHeader>

          {orgConfig && (
            <div className="space-y-6">
              {/* Stats Overview */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="card-inset">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="text-2xl font-bold">{orgConfig.phoneNumbers.length}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Phone Numbers</p>
                  </CardContent>
                </Card>
                <Card className="card-inset">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Mic className="h-4 w-4 text-muted-foreground" />
                      <span className="text-2xl font-bold">{orgConfig.assistants.length}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Assistants</p>
                  </CardContent>
                </Card>
                <Card className="card-inset">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-2xl font-bold">{orgConfig.organization.limits.monthlyVoiceMinutes}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Monthly Limit</p>
                  </CardContent>
                </Card>
              </div>

              {/* Phone Numbers */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-base">Phone Numbers</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowProvisionDialog(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Provision
                  </Button>
                </div>
                {orgConfig.phoneNumbers.length > 0 ? (
                  <div className="space-y-2">
                    {orgConfig.phoneNumbers.map((phone) => (
                      <div key={phone.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <span className="font-mono">{formatPhoneNumber(phone.phoneNumber)}</span>
                          <Badge variant="outline" className="ml-2">{phone.status}</Badge>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {phone.provider === 'telnyx' ? 'Voice' : 'Legacy'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground p-4 text-center border rounded-lg">
                    No phone numbers configured
                  </p>
                )}
              </div>

              {/* Assistants */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-base">AI Assistants</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAssistantDialog(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Create
                  </Button>
                </div>
                {orgConfig.assistants.length > 0 ? (
                  <div className="space-y-2">
                    {orgConfig.assistants.map((assistant) => (
                      <div key={assistant.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <span className="font-medium">{assistant.name}</span>
                          <p className="text-sm text-muted-foreground truncate max-w-md">
                            {assistant.firstMessage}
                          </p>
                        </div>
                        <Badge variant="outline">{assistant.voiceId}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground p-4 text-center border rounded-lg">
                    No assistants configured
                  </p>
                )}
              </div>

              {/* Recent Calls */}
              {orgConfig.recentCalls.length > 0 && (
                <div>
                  <Label className="text-base mb-2 block">Recent Calls</Label>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Direction</TableHead>
                          <TableHead>From/To</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Duration</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orgConfig.recentCalls.slice(0, 5).map((call) => (
                          <TableRow key={call.id}>
                            <TableCell>
                              {call.direction === 'inbound' ? (
                                <PhoneIncoming className="h-4 w-4 text-green-500" />
                              ) : (
                                <PhoneOutgoing className="h-4 w-4 text-blue-500" />
                              )}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {call.direction === 'inbound'
                                ? formatPhoneNumber(call.phoneNumberFrom)
                                : formatPhoneNumber(call.phoneNumberTo)}
                            </TableCell>
                            <TableCell>
                              <Badge variant={call.status === 'completed' ? 'default' : 'secondary'}>
                                {call.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {call.durationSeconds ? `${Math.floor(call.durationSeconds / 60)}m ${call.durationSeconds % 60}s` : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Provision Phone Number Dialog */}
      <Dialog open={showProvisionDialog} onOpenChange={setShowProvisionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Provision Phone Number</DialogTitle>
            <DialogDescription>
              Provision a new phone number for {selectedOrg?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="areaCode">Area Code</Label>
              <Input
                id="areaCode"
                placeholder="e.g., 512"
                value={provisionForm.areaCode}
                onChange={(e) => setProvisionForm({ ...provisionForm, areaCode: e.target.value.replace(/\D/g, '').slice(0, 3) })}
                maxLength={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="provider">Provider</Label>
              <Select
                value={provisionForm.preferredProvider}
                onValueChange={(val: string) => setProvisionForm({ ...provisionForm, preferredProvider: val as 'telnyx' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="telnyx">Telnyx</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="callDirection">Call Direction</Label>
              <Select
                value={provisionForm.callDirection}
                onValueChange={(val) => setProvisionForm({ ...provisionForm, callDirection: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inbound">Receptionist Only</SelectItem>
                  <SelectItem value="outbound">Follow-Up Only</SelectItem>
                  <SelectItem value="both">Full Coverage</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProvisionDialog(false)}>
              Cancel
            </Button>
            <Button onClick={provisionNumber} disabled={isSubmitting || provisionForm.areaCode.length !== 3}>
              {isSubmitting ? 'Provisioning...' : 'Provision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Assistant Dialog */}
      <Dialog open={showAssistantDialog} onOpenChange={setShowAssistantDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create AI Assistant</DialogTitle>
            <DialogDescription>
              Create a new AI assistant for {selectedOrg?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="assistantName">Name</Label>
              <Input
                id="assistantName"
                placeholder={`e.g., ${brand.appName}`}
                value={assistantForm.name}
                onChange={(e) => setAssistantForm({ ...assistantForm, name: e.target.value })}
              />
            </div>

            {assistantForm.firstMessage && (
            <div className="space-y-2">
              <Label>Greeting Preview</Label>
              <div className="p-3 bg-muted rounded-md text-sm text-muted-foreground italic">
                "{assistantForm.firstMessage}"
              </div>
              <p className="text-xs text-muted-foreground">
                Generated server-side from agent name and firm settings.
              </p>
            </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="systemPrompt">System Prompt (Optional)</Label>
              <Textarea
                id="systemPrompt"
                placeholder="Instructions for the AI assistant..."
                value={assistantForm.systemPrompt}
                onChange={(e) => setAssistantForm({ ...assistantForm, systemPrompt: e.target.value })}
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="voiceId">Voice</Label>
              <Select
                value={assistantForm.voiceId}
                onValueChange={(val) => setAssistantForm({ ...assistantForm, voiceId: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="jennifer">Jennifer (Professional Female)</SelectItem>
                  <SelectItem value="michael">Michael (Friendly Male)</SelectItem>
                  <SelectItem value="sarah">Sarah (Energetic Female)</SelectItem>
                  <SelectItem value="james">James (Authoritative Male)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssistantDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={createAssistant}
              disabled={isSubmitting || !assistantForm.name || !assistantForm.firstMessage}
            >
              {isSubmitting ? 'Creating...' : 'Create Assistant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
